import {isValidEmail} from '~/lib/validation';
import React, {useCallback, useState, useEffect, useRef} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import sy from '~/styles';
import {service_request as serviceRequestApi} from '~/api/private';
import {
  Dialog,
  FormTextInput,
  Text,
  Link,
  Checkbox,
  PrivateText,
} from '~/components/controls';
import Format from '~/lib/format';
import {useDispatch, useSelector} from '~/lib/hooks';
import {hideClientMessagesDialog, serviceRequestUpdate} from '~/actions';

import SmsIcon from '~/images/md-icons/sms/materialsymbolsoutlined/24px.svg';
import SmsFailedIcon from '~/images/md-icons/sms_failed/materialicons/24px.svg';
import MailIcon from '~/images/md-icons/mail/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';

const phoneRegex = /^06\d{8}$/i;

const DEFAULT_TEXTS = {
  Reserved: {
    title: 'Bevestiging afspraak',
    sms: {
      title: 'SMS bevestiging afspraak',
      body: (license, company_name, datetime) =>
        `Uw reservering op ${moment.utc(datetime).format('D-M')} om ${moment
          .utc(datetime)
          .format('HH:mm')} voor uw voertuig met kenteken ${Format.license(
          license,
        )} is bevestigd.`,
    },
    email: {
      title: 'Email bevestiging afspraak',
      body: (license, company_name, approved_tasks, datetime) =>
        `Uw reservering op ${moment.utc(datetime).format('D-M')} om ${moment
          .utc(datetime)
          .format('HH:mm')} voor uw voertuig met kenteken ${Format.license(
          license,
        )} is bevestigd. Mocht er iets veranderen dan vernemen we het graag.`,
    },
  },
  Done: {
    title: 'Werkzaamheden gereed',
    sms: {
      title: 'SMS auto gereed',
      body: (license, company_name) =>
        `De werkzaamheden aan de ${Format.license(
          license,
        )} zijn gereed. Mvg ${company_name}`,
    },
    email: {
      title: 'Email werkzaamheden',
      body: (license, company_name, approved_tasks) =>
        `De werkzaamheden aan de ${Format.license(
          license,
        )} zijn gereed. Dit is er uitgevoerd:

      ${approved_tasks.join('\n')}

Graag tot ziens bij ${company_name}!`,
    },
  },
};

const startStatusesPolling = ({poll, timerRef}) => {
  timerRef.current = setInterval(() => {
    poll().catch(() => {});
  }, 2000);
};

const StatusLink = ({status}) => {
  switch (status) {
    case 'Delivered':
      return (
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          &nbsp;&middot;&nbsp;Verzonden
        </Text>
      );

    case 'SentFailed':
      return (
        <Text style={[sy.smallRegular, {color: '#972727'}]}>
          &nbsp;&middot;&nbsp;Verzenden mislukt
        </Text>
      );

    default:
      return <></>;
  }
};
StatusLink.propTypes = {
  status: PropTypes.any,
};

const EditSmsDialog = ({
  status,
  reference_id,
  number,
  text,
  onDismiss,
  onChange,
}) => {
  const [smsNumber, setSmsNumber] = useState(number);
  const [smsText, setSmsText] = useState(text);
  const [phoneError, setPhoneError] = useState(false);

  const phoneMissing = !smsNumber?.length;
  const phoneInvalid = !!smsNumber?.length && !phoneRegex.test(smsNumber);
  let phoneErrorMessage = null;

  if (phoneMissing) {
    phoneErrorMessage = 'Mobiele telefoonnummer is verplicht';
  } else if (phoneInvalid) {
    phoneErrorMessage = 'Ongeldige waarde mobiele telefoonnummer';
  }

  return (
    <Dialog
      visible={true}
      title={DEFAULT_TEXTS[status]?.sms.title}
      titleIcon={<SmsIcon fill="#4a4a49" />}
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Annuleren',
          onPress: onDismiss,
        },
        {
          text: 'OK',
          disabled: phoneError,
          onPress: () => {
            if (phoneError) {
              return;
            }

            onChange(smsNumber, smsText);
            onDismiss();
          },
        },
      ]}
      options={{
        noPaddingContent: true,
      }}>
      <View style={[sy['p-4'], sy['gap-4']]}>
        <PrivateText>
          <FormTextInput
            value={smsNumber}
            onChangeText={setSmsNumber}
            onEndEditing={() => {
              const valid = !smsNumber || !!smsNumber.match(phoneRegex);
              setPhoneError(!valid);
            }}
            label="Mobiele telefoonnummer"
            hasError={phoneError}
            errorMessage={phoneErrorMessage}
            inputProps={{
              maxLength: 10,
              keyboardType: 'numeric',
            }}
          />
        </PrivateText>
        <View style={[sy['gap-1']]}>
          <FormTextInput
            label="Tekst"
            value={smsText}
            disabled={!!reference_id}
            multiline={true}
            onChangeText={(text) => setSmsText(text.substring(0, 160))}
            inputProps={{
              multiline: true,
              maxLength: 160,
              numberOfLines: 3,
            }}
          />
          <Text style={[sy.smallRegular, sy['text-lightgray'], sy['pl-2']]}>
            {smsText.length} / 160
          </Text>
        </View>
      </View>
    </Dialog>
  );
};
EditSmsDialog.propTypes = {
  status: PropTypes.any,
  reference_id: PropTypes.any,
  number: PropTypes.any,
  text: PropTypes.any,
  onDismiss: PropTypes.any,
  onChange: PropTypes.any,
};

const EditEmailDialog = ({status, email, body, onDismiss, onChange}) => {
  const [emailAddress, setEmailAddress] = useState(email);
  const [emailError, setEmailError] = useState(false);

  return (
    <Dialog
      visible={true}
      title={DEFAULT_TEXTS[status]?.email.title}
      titleIcon={<MailIcon fill="#4a4a49" />}
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Annuleren',
          onPress: onDismiss,
        },
        {
          text: 'OK',
          disabled: emailError,
          onPress: () => {
            if (emailError) {
              return;
            }

            onChange(emailAddress);
            onDismiss();
          },
        },
      ]}
      options={{
        noPaddingContent: true,
      }}>
      <View style={[sy['p-4'], sy['gap-4']]}>
        <PrivateText>
          <FormTextInput
            label="E-mail"
            value={emailAddress}
            onChangeText={setEmailAddress}
            onEndEditing={() => {
              const valid = isValidEmail(emailAddress);
              setEmailError(!valid);
            }}
            hasError={emailError}
            errorMessage="Email heeft een ongeldige waarde"
            inputProps={{
              keyboardType: 'email-address',
            }}
          />
        </PrivateText>
        <FormTextInput
          label="Tekst"
          value={body}
          disabled={true}
          multiline={true}
          inputProps={{
            multiline: true,
            numberOfLines: 9,
          }}
        />
      </View>
    </Dialog>
  );
};
EditEmailDialog.propTypes = {
  status: PropTypes.any,
  email: PropTypes.any,
  body: PropTypes.any,
  onDismiss: PropTypes.any,
  onChange: PropTypes.any,
};

const ClientMessagesDialog = () => {
  const dispatch = useDispatch();

  const {visible, status, request_id} =
    useSelector((state) => state.client_messages_dialog) ?? {};

  const [showEditSms, setShowEditSms] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [smsText, setSmsText] = useState(null);
  const [smsNumber, setSmsNumber] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [emailText, setEmailText] = useState(null);
  const [references, setReferences] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [selected, setSelected] = useState({});

  const timerID = useRef(null);
  const statusesRef = useRef(null);

  useEffect(() => {
    statusesRef.current = statuses;
  }, [statuses]);

  const pollStatuses = useCallback(async () => {
    const {result: nextStatuses} =
      await serviceRequestApi.client_message_statuses(request_id, references);

    if (!isEqual(statusesRef.current, nextStatuses)) {
      dispatch(
        serviceRequestUpdate({
          request_id,
        }),
      );
    }

    setStatuses(nextStatuses);
  }, [dispatch, references, request_id]);

  const onDismiss = useCallback(() => {
    dispatch(hideClientMessagesDialog());
  }, [dispatch]);

  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.get(request_id, dispatch);
      const {
        datetime,
        phone_number,
        email,
        license,
        company_name,
        email_enabled,
      } = result[0];

      const approved_tasks = result[0].components
        .flatMap((c) => c.tasks)
        .map((task) => {
          if (
            (result[0].fleet_request ?? result[0].rob)?.components[task.id]
              ?.status?.code === 'Approved'
          ) {
            return task.description;
          }

          return null;
        })
        .filter(Boolean);

      setSmsNumber(phone_number);
      setSmsText(
        DEFAULT_TEXTS[status]?.sms.body(license, company_name, datetime),
      );

      if (email_enabled) {
        setEmailAddress(email);
        setEmailText(
          DEFAULT_TEXTS[status]?.email.body(
            license,
            company_name,
            approved_tasks,
            datetime,
          ),
        );
      }
    };

    if (visible) {
      fetch().catch(() => {});
    } else {
      if (timerID.current) {
        clearInterval(timerID.current);
      }

      setShowEditSms(false);
      setReferences(null);
      setStatuses(null);
    }
  }, [dispatch, request_id, status, visible]);

  useEffect(() => {
    setSelected({
      sms: !!smsNumber?.length,
      email: !!emailAddress?.length,
    });
  }, [smsNumber, emailAddress]);

  useEffect(() => {
    if (references) {
      if (timerID.current) {
        clearInterval(timerID.current);
      }

      startStatusesPolling({poll: pollStatuses, timerRef: timerID});
    }

    const intervalId = timerID.current;

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pollStatuses, references]);

  if (showEditSms) {
    return (
      <EditSmsDialog
        reference_id={references?.sms}
        number={smsNumber}
        text={smsText}
        onDismiss={() => setShowEditSms(false)}
        onChange={(number, text) => {
          setSmsNumber(number);
          setSmsText(text);
        }}
      />
    );
  }

  if (showEditEmail) {
    return (
      <EditEmailDialog
        email={emailAddress}
        body={emailText}
        onDismiss={() => setShowEditEmail(false)}
        onChange={setEmailAddress}
      />
    );
  }

  return (
    <Dialog
      visible={visible}
      title={DEFAULT_TEXTS[status]?.title}
      onDismiss={onDismiss}
      buttons={[
        {
          text: 'Sluiten',
          onPress: onDismiss,
        },
        {
          text: 'Verzenden',
          onPress: async () => {
            let email = null;
            let sms = null;

            if (selected.email) {
              ({reference_id: email} = await serviceRequestApi.email(
                request_id,
                status,
                emailAddress,
              ));
            }

            if (selected.sms) {
              ({reference_id: sms} = await serviceRequestApi.sms(
                request_id,
                smsNumber,
                smsText,
              ));
            }

            setReferences({
              email,
              sms,
            });

            onDismiss();
          },
        },
      ]}>
      <View style={[sy['py-4'], sy['gap-4']]}>
        {emailAddress?.length > 0 && (
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <Checkbox
              checked={selected.email}
              disabledTimeout={0}
              onPress={() => {
                setSelected((prevState) => ({
                  ...prevState,
                  email: !prevState.email,
                }));
              }}
            />
            <View style={[sy['flex-1']]}>
              <Text>Email</Text>
              <PrivateText style={[sy.smallRegular, sy['text-lightgray']]}>
                {emailAddress}
              </PrivateText>
              <View style={sy['flex-row']}>
                <Link
                  textStyle={[
                    sy.smallRegular,
                    sy['no-underline'],
                    {color: '#2907E3'},
                  ]}
                  onPress={() => setShowEditEmail(true)}>
                  {references?.email ? 'Bekijken' : 'Bewerken'}
                </Link>
                {references?.email && <StatusLink status="Delivered" />}
              </View>
            </View>
            <MailIcon fill="#4a4a49" />
          </View>
        )}
        {smsNumber?.length > 0 && (
          <>
            {emailAddress?.length > 0 && <Divider style={sy['bg-gray88']} />}
            <View style={[sy['flex-row'], sy['gap-4']]}>
              <Checkbox
                checked={selected.sms}
                disabledTimeout={0}
                onPress={() => {
                  setSelected((prevState) => ({
                    ...prevState,
                    sms: !prevState.sms,
                  }));
                }}
              />
              <View style={[sy['flex-1']]}>
                <Text>SMS</Text>
                <PrivateText style={[sy.smallRegular, sy['text-lightgray']]}>
                  {smsNumber}&nbsp;&middot;&nbsp;Kosten {Format.price(0.09)}
                </PrivateText>
                <View style={sy['flex-row']}>
                  <Link
                    textStyle={[
                      sy.smallRegular,
                      sy['no-underline'],
                      {color: '#2907E3'},
                    ]}
                    onPress={() => setShowEditSms(true)}>
                    {references?.sms ? 'Bekijken' : 'Bewerken'}
                  </Link>
                  {references?.sms && <StatusLink status={statuses?.sms} />}
                </View>
              </View>
              {statuses?.sms === 'SentFailed' ? (
                <SmsFailedIcon fill="#972727" />
              ) : (
                <SmsIcon fill="#4a4a49" />
              )}
            </View>
          </>
        )}
      </View>
    </Dialog>
  );
};
ClientMessagesDialog.propTypes = {};

export default ClientMessagesDialog;
